/**
 * RBAC v2 — nouvelle taxonomie de permissions (derrière le flag Flipt `permission-v2`).
 *
 * Modélisation explicite « à la CASL » : un unique objet général `rbacV2Abilities` liste chaque
 * ressource (clé concaténée `module.sous-onglet.fonctionnalité`) → tableau EXACT de ses actions.
 * On connaît ainsi précisément, au type-level, quelles actions existent sur chaque ressource.
 *
 * - Les actions partagées (`view/add/edit/delete/export/import`) existent sur plusieurs ressources.
 * - Chaque action spéciale est une action nommée à part entière, portée par UNE seule ressource.
 *
 * La notion de `scope` v1 n'existe plus en v2. La clé de ressource est stockée telle quelle dans
 * `role_permissions.resource`, et l'action dans `role_permissions.permission_type` (scope = NULL).
 *
 * Le module de permissions v1 (`./rbac.ts`) reste inchangé et coexiste avec cette v2.
 */

/** `feature_flags`/Flipt : gate d'activation de l'UI et du modèle v2. */
export const PERMISSION_V2_FLAG = 'permission-v2' as const

/** Actions partagées par plusieurs ressources (colonnes fixes de la matrice). */
export const RBAC_V2_SHARED_ACTIONS = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * Actions spéciales : chacune n'est portée que par la ressource à laquelle elle est associée.
 * (Colonne « Action spécifique » de la matrice.)
 */
export const RBAC_V2_SPECIAL_ACTIONS = [
  'launch-reappro-simulation',
  'regularize-payment-default',
  'assign-bo-role',
  'generate-promo-codes',
  'validate-inventory',
  'sync-schedule',
  'restart-kiosk',
  'enter-support-mode',
  'launch-sales-calculation',
  'validate-clocking',
  'resend-unread-sms',
  'archive-real-estate-project',
] as const

export const RBAC_V2_ACTIONS = [...RBAC_V2_SHARED_ACTIONS, ...RBAC_V2_SPECIAL_ACTIONS] as const

export type RbacV2SharedAction = (typeof RBAC_V2_SHARED_ACTIONS)[number]
export type RbacV2SpecialAction = (typeof RBAC_V2_SPECIAL_ACTIONS)[number]
export type RbacV2Action = RbacV2SharedAction | RbacV2SpecialAction

const FULL = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * OBJET GÉNÉRAL — source de vérité de la taxonomie v2.
 * Clé = `module.sous-onglet.fonctionnalité` (= valeur stockée dans `role_permissions.resource`).
 * Valeur = tableau exact des actions disponibles pour cette ressource.
 * Un tableau vide = fonctionnalité présente dans la carte mais sans action configurable (module désactivé).
 */
export const rbacV2Abilities = {
  // 1. Tableau de bord
  'dashboard.dashboard.global-indicators': FULL,

  // 2. Aujourd'hui
  'today.today.daily-tracking': FULL,

  // 3. Paniers
  'carts.carts.cart': FULL,

  // 4. Catégories — module migré : chaque action listée correspond à une affordance réelle
  'categories.categories.standard': ['read', 'add', 'edit', 'delete'],
  'categories.internal-categories.internal': ['read', 'add', 'edit', 'delete', 'export'],
  'categories.reappro.category': ['read', 'add', 'edit', 'delete'],
  'categories.reappro.config': ['read', 'edit', 'delete', 'export', 'import'],
  'categories.reappro.simulation': ['export', 'import', 'launch-reappro-simulation'],
  'categories.brands.brand': ['read', 'add', 'edit', 'delete'],

  // 5. Produits
  'products.products.product': FULL,
  'products.products.price': FULL,
  'products.products.packaging': FULL,
  'products.products.menu-eligibility': FULL,

  // 6. Stock Plateforme
  'platform-stock.platform-stock.stock': FULL,

  // 7. Gestion des prix
  'pricing.pricing.store-pricing': FULL,

  // 8. Utilisateurs
  'users.app-users.user': FULL,
  'users.app-users.ban': FULL,
  'users.app-users.solvency': FULL,
  'users.students.student': FULL,
  'users.payment-defaults.default': FULL,
  'users.payment-defaults.regularization': [...FULL, 'regularize-payment-default'],
  'users.bo-users.user': FULL,
  'users.bo-users.role-assignment': [...FULL, 'assign-bo-role'],

  // 9. Campagne promo
  'promo.temporary-campaigns.campaign': FULL,
  'promo.temporary-campaigns.single-use-codes': [...FULL, 'generate-promo-codes'],
  'promo.student-promo.student-promo': FULL,

  // 10. Partenariats CE
  'works-council.works-council.partnership': FULL,

  // 11. Notifications (module désactivé)
  'notifications.notifications.notification': [],
  'notifications.notifications.duplication': [],

  // 12. Boutiques
  'stores.store-list.store': FULL,
  'stores.store-stocks.stock': FULL,
  'stores.current-inventory.inventory': FULL,
  'stores.current-inventory.validation': [...FULL, 'validate-inventory'],
  'stores.all-inventories.history': FULL,
  'stores.schedules.schedule': FULL,
  'stores.schedules.sync': [...FULL, 'sync-schedule'],
  'stores.kiosks.kiosk': FULL,
  'stores.kiosks.restart': [...FULL, 'restart-kiosk'],
  'stores.statistics.statistics': FULL,

  // 13. Comptabilité (module désactivé)
  'accounting.accounting.figures': [],
  'accounting.accounting.balance': [],
  'accounting.accounting.payments': [],
  'accounting.accounting.works-council-history': [],
  'accounting.accounting.warnings': [],

  // 14. Qualité
  'quality.search-by-lot.lot-traceability': FULL,
  'quality.search-by-article.article-traceability': FULL,

  // 15. Contrôle panier
  'cart-control.cart-control.live-control': FULL,
  'cart-control.cart-control.support-mode': [...FULL, 'enter-support-mode'],

  // 16. Ventes journalières
  'daily-sales.daily-sales.daily-sales': FULL,
  'daily-sales.daily-sales.auto-calculation': [...FULL, 'launch-sales-calculation'],

  // 17. Vendeurs (module désactivé)
  'sellers.sellers.seller': [],
  'sellers.establishments.establishment': [],

  // 18. Caméras
  'cameras.cameras.stream': FULL,
  'cameras.cameras.config': FULL,

  // 19. Capteurs (pas d'export/import)
  'sensors.sensors.sensor': ['read', 'add', 'edit', 'delete'],
  'sensors.fridges.fridge': ['read', 'add', 'edit', 'delete'],

  // 20. RH & Planning
  'hr.planning.weekly-planning': FULL,
  'hr.staff.collaborator': FULL,
  'hr.clocking.clocking': [...FULL, 'validate-clocking'],
  'hr.absences.absence': FULL,

  // 21. Suivi des SMS
  'sms.sms.sent': ['read', 'export'],
  'sms.sms.resend': ['read', 'resend-unread-sms'],

  // 22. Contrôle Interventions (Visio) (module désactivé)
  'visio.yesterday-interactions.interaction': [],
  'visio.exchanges-to-evaluate.exchange': [],
  'visio.exchanges-to-evaluate.evaluation': [],
  'visio.overview.overview': [],
  'visio.administration.administration': [],

  // 23. Foncier & travaux
  'real-estate.map.map': FULL,
  'real-estate.kanban.project': FULL,
  'real-estate.kanban.archive': [...FULL, 'archive-real-estate-project'],
  'real-estate.tasks.task': FULL,
  'real-estate.calendar.calendar': FULL,
  'real-estate.contacts.contact': FULL,
  'real-estate.areas-of-interest.area': FULL,

  // 24. Réseau & Boutiques
  'network.network.network-dashboard': FULL,

  // 25. Listes de diffusion
  'mailing-lists.mailing-lists.mailing-list': FULL,

  // 26. Administration › Rôles (module désactivé)
  'roles-admin.roles.role': [],
  'roles-admin.roles.admin-assignment': [],

  // 27. Feature Flags (module désactivé)
  'feature-flags.feature-flags.feature-flag': [],
} as const satisfies Record<string, readonly RbacV2Action[]>

export type RbacV2Resource = keyof typeof rbacV2Abilities

/**
 * Union de tuples `[action, ressource]` dérivée de `rbacV2Abilities`.
 * Prête pour typer un `MongoAbility<RbacV2AbilityTuple>` si l'enforcement v2 est câblé plus tard.
 */
export type RbacV2AbilityTuple = {
  [K in RbacV2Resource]: [(typeof rbacV2Abilities)[K][number], K]
}[RbacV2Resource]

export const rbacV2Resources = Object.keys(rbacV2Abilities) as RbacV2Resource[]

/** Valide qu'une action est bien autorisée pour une ressource donnée (bord d'écriture). */
export const isRbacV2ActionAllowed = (resource: string, action: string): boolean =>
  (rbacV2Abilities as Record<string, readonly string[]>)[resource]?.includes(action) ?? false

/** Renvoie l'action spéciale portée par une ressource, s'il y en a une. */
export const getRbacV2SpecialAction = (resource: RbacV2Resource): RbacV2SpecialAction | undefined =>
  (rbacV2Abilities[resource] as readonly RbacV2Action[]).find(
    (action): action is RbacV2SpecialAction =>
      (RBAC_V2_SPECIAL_ACTIONS as readonly string[]).includes(action),
  )

// --- Libellés (français) — maps plats, ordre de déclaration = ordre d'affichage --------------

/** Clé = segment `module` de la clé de ressource. */
export const RBAC_V2_MODULE_LABELS: Record<string, string> = {
  dashboard: '1. Tableau de bord',
  today: "2. Aujourd'hui",
  carts: '3. Paniers',
  categories: '4. Catégories',
  products: '5. Produits',
  'platform-stock': '6. Stock Plateforme',
  pricing: '7. Gestion des prix',
  users: '8. Utilisateurs',
  promo: '9. Campagne promo',
  'works-council': '10. Partenariats CE',
  notifications: '11. Notifications',
  stores: '12. Boutiques',
  accounting: '13. Comptabilité',
  quality: '14. Qualité',
  'cart-control': '15. Contrôle panier',
  'daily-sales': '16. Ventes journalières',
  sellers: '17. Vendeurs',
  cameras: '18. Caméras',
  sensors: '19. Capteurs',
  hr: '20. RH & Planning',
  sms: '21. Suivi des SMS',
  visio: '22. Contrôle Interventions (Visio)',
  'real-estate': '23. Foncier & travaux',
  network: '24. Réseau & Boutiques',
  'mailing-lists': '25. Listes de diffusion',
  'roles-admin': '26. Administration › Rôles',
  'feature-flags': '27. Feature Flags',
}

/** Clé = `module.sous-onglet`. */
export const RBAC_V2_SUBTAB_LABELS: Record<string, string> = {
  'dashboard.dashboard': 'Tableau de bord',
  'today.today': "Aujourd'hui",
  'carts.carts': 'Paniers',
  'categories.categories': 'Catégories',
  'categories.internal-categories': 'Catégories internes',
  'categories.reappro': 'Catégories réappro',
  'categories.brands': 'Marques',
  'products.products': 'Produits',
  'platform-stock.platform-stock': 'Stock Plateforme',
  'pricing.pricing': 'Gestion des prix',
  'users.app-users': "Utilisateurs de l'app",
  'users.students': 'Étudiants',
  'users.payment-defaults': 'Défauts de paiement',
  'users.bo-users': 'Utilisateurs du BO',
  'promo.temporary-campaigns': 'Campagnes temporaires',
  'promo.student-promo': 'Promotion étudiante',
  'works-council.works-council': 'Partenariats CE',
  'notifications.notifications': 'Notifications',
  'stores.store-list': 'Liste boutiques',
  'stores.store-stocks': 'Boutique › Stocks',
  'stores.current-inventory': 'Boutique › Inventaire en cours',
  'stores.all-inventories': 'Boutique › Tous les inventaires',
  'stores.schedules': 'Boutique › Gestion des horaires',
  'stores.kiosks': 'Boutique › Gestion des kiosks',
  'stores.statistics': 'Boutique › Statistiques',
  'accounting.accounting': 'Comptabilité',
  'quality.search-by-lot': 'Recherche par lot',
  'quality.search-by-article': 'Recherche par article',
  'cart-control.cart-control': 'Contrôle panier',
  'daily-sales.daily-sales': 'Ventes journalières',
  'sellers.sellers': 'Vendeurs',
  'sellers.establishments': 'Établissements',
  'cameras.cameras': 'Caméras',
  'sensors.sensors': 'Capteurs',
  'sensors.fridges': 'Réfrigérateurs',
  'hr.planning': 'Planning',
  'hr.staff': 'Personnel',
  'hr.clocking': 'Pointage',
  'hr.absences': 'Calendrier Absences',
  'sms.sms': 'Suivi des SMS',
  'visio.yesterday-interactions': 'Interactions de la Veille',
  'visio.exchanges-to-evaluate': 'Échanges à Évaluer',
  'visio.overview': "Vue d'ensemble",
  'visio.administration': 'Administration (Visio)',
  'real-estate.map': 'Carte',
  'real-estate.kanban': 'Kanban',
  'real-estate.tasks': 'Tâches',
  'real-estate.calendar': 'Calendrier',
  'real-estate.contacts': 'Contacts',
  'real-estate.areas-of-interest': "Zones d'intérêt",
  'network.network': 'Réseau & Boutiques',
  'mailing-lists.mailing-lists': 'Listes de diffusion',
  'roles-admin.roles': 'Rôles',
  'feature-flags.feature-flags': 'Feature Flags',
}

/** Clé = clé de ressource complète. */
export const RBAC_V2_FEATURE_LABELS: Record<RbacV2Resource, string> = {
  'dashboard.dashboard.global-indicators': 'Indicateurs globaux',
  'today.today.daily-tracking': 'Suivi du jour (classements ventes qté/CA)',
  'carts.carts.cart': 'Paniers (liste & détail)',
  'categories.categories.standard': 'Catégorie standard',
  'categories.internal-categories.internal': 'Catégorie interne',
  'categories.reappro.category': 'Catégorie de réappro',
  'categories.reappro.config': 'Configuration de réappro (stocks min)',
  'categories.reappro.simulation': 'Simulation générale de réappro',
  'categories.brands.brand': 'Marque',
  'products.products.product': 'Produit (fiche, identité, catégories)',
  'products.products.price': 'Prix produit (achat, vente, TVA, poids)',
  'products.products.packaging': 'Colisage produit',
  'products.products.menu-eligibility': 'Éligibilité menus & suppléments',
  'platform-stock.platform-stock.stock': 'Stock plateforme (quantités & lots)',
  'pricing.pricing.store-pricing': 'Prix par boutique',
  'users.app-users.user': "Utilisateur de l'app",
  'users.app-users.ban': 'Bannissement utilisateur',
  'users.app-users.solvency': 'Solvabilité & moyens de paiement (Conecs)',
  'users.students.student': 'Étudiant',
  'users.payment-defaults.default': 'Défaut de paiement',
  'users.payment-defaults.regularization': "Régularisation d'un défaut",
  'users.bo-users.user': 'Utilisateur du BO',
  'users.bo-users.role-assignment': 'Attribution de rôle à un utilisateur BO',
  'promo.temporary-campaigns.campaign': 'Campagne temporaire',
  'promo.temporary-campaigns.single-use-codes': 'Codes promo à usage unique',
  'promo.student-promo.student-promo': 'Promotion étudiante',
  'works-council.works-council.partnership': 'Partenariat CE',
  'notifications.notifications.notification': 'Notification',
  'notifications.notifications.duplication': 'Duplication de notification',
  'stores.store-list.store': 'Boutique',
  'stores.store-stocks.stock': 'Stock / valorisation / anti-gaspi boutique',
  'stores.current-inventory.inventory': 'Inventaire boutique',
  'stores.current-inventory.validation': "Validation d'inventaire",
  'stores.all-inventories.history': 'Historique des inventaires',
  'stores.schedules.schedule': 'Horaires boutique',
  'stores.schedules.sync': 'Synchronisation des horaires',
  'stores.kiosks.kiosk': 'Kiosk / borne',
  'stores.kiosks.restart': "Redémarrage d'un kiosk",
  'stores.statistics.statistics': 'Statistiques boutique',
  'accounting.accounting.figures': 'Chiffres comptables (ventes, TVA, impayés)',
  'accounting.accounting.balance': 'Bilan comptable',
  'accounting.accounting.payments': 'Paiements',
  'accounting.accounting.works-council-history': 'Historiques de CE',
  'accounting.accounting.warnings': 'Avertissements comptables',
  'quality.search-by-lot.lot-traceability': 'Traçabilité par lot',
  'quality.search-by-article.article-traceability': 'Traçabilité par article',
  'cart-control.cart-control.live-control': 'Contrôle panier en direct',
  'cart-control.cart-control.support-mode': 'Mode support (prise de main caisse)',
  'daily-sales.daily-sales.daily-sales': 'Ventes journalières',
  'daily-sales.daily-sales.auto-calculation': 'Calcul automatique des ventes',
  'sellers.sellers.seller': 'Vendeur',
  'sellers.establishments.establishment': 'Établissement',
  'cameras.cameras.stream': 'Flux caméras (par boutique)',
  'cameras.cameras.config': 'Configuration des caméras',
  'sensors.sensors.sensor': 'Capteur',
  'sensors.fridges.fridge': 'Réfrigérateur',
  'hr.planning.weekly-planning': 'Planning hebdomadaire (créneaux)',
  'hr.staff.collaborator': 'Fiche collaborateur',
  'hr.clocking.clocking': 'Pointage',
  'hr.absences.absence': 'Absence',
  'sms.sms.sent': 'SMS envoyés',
  'sms.sms.resend': 'Relance des SMS non-lus',
  'visio.yesterday-interactions.interaction': 'Interactions de la veille',
  'visio.exchanges-to-evaluate.exchange': 'Échange à évaluer',
  'visio.exchanges-to-evaluate.evaluation': "Évaluation d'un échange",
  'visio.overview.overview': "Vue d'ensemble (boutique / opérateur)",
  'visio.administration.administration': 'Administration du module Visio',
  'real-estate.map.map': 'Carte foncière & indicateurs',
  'real-estate.kanban.project': 'Projet foncier (pipeline)',
  'real-estate.kanban.archive': "Archivage/reprise d'un projet",
  'real-estate.tasks.task': 'Tâche',
  'real-estate.calendar.calendar': 'Calendrier foncier',
  'real-estate.contacts.contact': 'Contact',
  'real-estate.areas-of-interest.area': "Zone d'intérêt",
  'network.network.network-dashboard': 'Tableau de bord réseau (comparaison)',
  'mailing-lists.mailing-lists.mailing-list': 'Liste de diffusion & destinataires',
  'roles-admin.roles.role': 'Rôle & permissions',
  'roles-admin.roles.admin-assignment': "Attribution d'admins à un rôle",
  'feature-flags.feature-flags.feature-flag': 'Feature flag (activation/désactivation)',
}

/** Libellé de chaque action (partagée ou spéciale). */
export const RBAC_V2_ACTION_LABELS: Record<RbacV2Action, string> = {
  read: 'Voir',
  add: 'Ajouter',
  edit: 'Modifier',
  delete: 'Supprimer',
  export: 'Exporter',
  import: 'Importer',
  'launch-reappro-simulation': 'Lancer la simulation',
  'regularize-payment-default': 'Traiter / régulariser',
  'assign-bo-role': 'Assigner un rôle',
  'generate-promo-codes': 'Générer les codes',
  'validate-inventory': "Valider l'inventaire",
  'sync-schedule': 'Enregistrer & synchroniser',
  'restart-kiosk': 'Redémarrer le kiosk',
  'enter-support-mode': 'Passer en mode support',
  'launch-sales-calculation': 'Lancer le calcul',
  'validate-clocking': 'Valider le pointage',
  'resend-unread-sms': 'Relancer les non-lus',
  'archive-real-estate-project': 'Archiver / reprendre',
}

// --- DTOs (permissions de rôle v2) ------------------------------------------------------------

export interface RbacV2RolePermission {
  resource: RbacV2Resource
  action: RbacV2Action
}

export interface ReplaceRbacV2RolePermissionsRequest {
  permissions: RbacV2RolePermission[]
}
